import { lazy } from "react";

/**
 * Lazy-loaded route pages
 * -----------------------
 * Each page module becomes its own JS chunk, loaded the first time the page
 * is opened.
 *
 * After a deploy, an already-open tab may ask for chunk files that no longer
 * exist (the S3 sync runs with --delete). In that case the page reloads once
 * to pick up the new build instead of showing a broken screen; a second
 * failure is thrown to the router's error boundary as usual.
 */
const RELOAD_FLAG = "chunk_reload";

const storage = {
  get: () => {
    try {
      return sessionStorage.getItem(RELOAD_FLAG);
    } catch {
      return null;
    }
  },
  set: () => {
    try {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      return true;
    } catch {
      return false; // can't remember the reload: don't risk a reload loop
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // ignore
    }
  },
};

// A chunk that could not be fetched. Vite's import() rejects with a TypeError
// whose message depends on the browser (webpack used to throw ChunkLoadError).
const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;
export const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" || CHUNK_ERROR.test(String(error?.message ?? ""));

export const importPage = (load) =>
  load()
    .then((module) => {
      storage.clear();
      return module;
    })
    .catch((error) => {
      if (isChunkLoadError(error) && !storage.get() && storage.set()) {
        window.location.reload();
        return new Promise(() => {}); // keep the loader up until the reload
      }
      throw error;
    });

/** lazy() for a module's default export */
export const lazyPage = (load) => lazy(() => importPage(load));

/** lazy() for a named export, e.g. lazyNamed(() => import("features/users"), "AddUser") */
export const lazyNamed = (load, name) =>
  lazy(() => importPage(load).then((module) => ({ default: module[name] })));
